import { defineStore } from 'pinia';
import type { IOktmo } from '~packages/epk-front-entities';
import { scopeMapping } from '~packages/epk-front-shared/src/lib/scope/scope-timestamp';

export const useUserStore = defineStore('user', {
  state: () => {
    return {
      userList: [] as UserInfo[],
      user: null as UserInfo | null,
      count: 0 as number,
      cardLph: {
        point_code: BigInt(0),
        isCreate: true as boolean,
        personalAccount: [] as PersonalInfo[],
        dim_period: '' as string,
        dim_system_type_lph: 0 as number,
        accountMainFio: '' as string,
        accountMainInn: '' as string,
        accountMainPassportSn: '' as string | number,
        accountMainBirthDay: '' as string,
        accountMainPassportDate: '' as string,
        accountMainPassportIssuedBy: '' as string,
        accountMainAddress: '' as string,
        personalAccountLocality: '' as string,
        personalAccountOktmo: '' as string,
        bookNumber: 0 as number,
      },
      consolidatedHouseholdBooks: null as ConsolidatedHouseholdBooks | null,
      newArray: [] as Array<object>,
      newArrayRegistry: [] as Array<object>,
      transferPowers: false,
      newArrayTransfer: [] as Array<object>,
      countValue: 0 as number,
      dateValue: '' as string,
      dateValueAnnual: '' as string,
    };
  },

  actions: {
    increment() {
      this.count++;
    },
    setDateValue(item: string) {
      console.log(item, 'item');
      this.dateValue = item;
    },
    setDateValueAnnual(item: string) {
      this.dateValueAnnual = item;
    },
    setNewValueOption(item: number) {
      this.countValue = item;
    },
    setNewArrayRegistry(items: Array<object>) {
      this.newArrayRegistry = items;
    },
    setNewArrayTransfer(items: Array<object>) {
      this.newArrayTransfer = { ...items };
    },
    setNewArray(items: Array<object>) {
      this.newArray = items;
    },

    setPersonalAccount(item: Partial<typeof this.cardLph>, clear = false) {
      if (clear) {
        for (const [key, value] of Object.entries(this.cardLph)) {
          const type = typeof value;
          if (type === 'object') {
            if (Array.isArray(value)) this.cardLph[key].length = 0;
            else this.cardLph[key] = {};
          } else if (type !== 'number') {
            this.cardLph[key] = '';
          } else if (type === 'number') {
            this.cardLph[key] = 0;
          }
        }
      } else {
        this.cardLph = Object.assign(this.cardLph, item);
      }
    },
  },

  getters: {
    getTransferPowers: state => state.transferPowers,
  },
});

interface UserInfo {
  name: string;
  age: number;
}

export interface PersonalInfo {
  id?: number;
  dim_personal_account?: string;
  lands_address?: string;
  lands_area?: string;
  lands_doc_cad_number?: string;
  lands_part_of_owner?: string | number;
  lands_doc_date?: string;
  lands_doc_name?: string;
  lands_doc_sn?: string;
  lands_nsiLandCategories_code?: number;
  lands_nsiLandCategories_name?: string;
  lands_nsiTypeOfPermittedUseLand_code?: number;
  lands_nsiTypeOfPermittedUseLand_name?: string;
  lands_nsiTypeOfRuleToLand_code?: number;
  lands_nsiTypeOfRuleToLand_name?: string;
  lands_owner_fio?: string;
  req_close_reason?: string;
  req_code_oktmo?: number;
  req_code_oktmo_short?: number;
  req_nsiLargeOKTMO_locality?: string;
  req_date_close?: string;
  req_date_open?: string;
  req_period_appear?: string;
  req_delete_mark?: boolean;
  req_is_broken?: number | boolean;
  req_is_close?: boolean;
  req_is_general?: boolean;
  req_open_reason?: string;
  disable?: boolean;
}

export interface CacheMap {
  title_oktmo: BaseReport;
  lph_info: BaseReport;
  members: BaseReport;
  lands_rules: BaseReport;
  animals_count: BaseReport;
  machine_count: BaseReport;
  dwelling_area_house_busy: BaseReport;
  money_plants: BaseReport;
  money_animals: BaseReport;
  economic_product_money: BaseReport;
  spok_membership: BaseReport;
  doc_add_info: BaseReport;
}

interface BaseReport {
  base_report: { cache_time: string };
}

interface DocDimensions {
  dim_epk_code: number;
  dim_period_end: string;
  dim_period_start: string;
}

interface DocReqs extends ReqFiles {
  req_time_stamp: string;
}

export interface ReqFiles {
  req_file_signed_one_year: string;
  req_file_print_latest_pdf: string[];
  req_file_print_latest_xlsx: string[];
  req_file_print_latest_pdf_list: string[];
  req_file_print_latest_xlsx_list: string[];
}

export interface ConsolidatedHouseholdBooks {
  cache_map: CacheMap;
  doc_dimensions: DocDimensions;
  doc_reqs: DocReqs;
  storage_id: string;
}

export interface SvodFive {
  svod_five_file_signed: string;
  svod_file_five_year_pdf: string[];
  svod_file_five_year_pdf_list: string[];
  svod_file_five_year_xlsx: string[];
  svod_file_five_year_xlsx_list: string[];
}

export interface IHousehold {
  code?: number;
  year_start: string;
  year_end: string;
  oktmo_oktmo: string[];
  oktmo_locality: string[];
  doc_date: string;
  svod_five?: SvodFive;
  reqs?: Record<string, unknown>;
  dimensions?: DocDimensions;
  oktmos?: IOktmo[];
}

export type Scope = keyof typeof scopeMapping;
